import { CheckCircle2, Database, Lightbulb } from 'lucide-react'
import type { DataSource, PitchDocument } from '../types'

const UNAVAILABLE_HINTS: Record<string, string> = {
  'Fundraising status': 'Set your fundraising status in your profile to start pitching',
  'Pitch Deck': 'Upload your pitch deck for a more accurate evaluation',
  Documents: 'Add more documents to your data room for deeper analysis',
  'Team profiles': 'Add LinkedIn profiles for all team members to strengthen your review',
  'Public web': 'Add your startup website so we can analyze your public presence',
}

function unavailableHint(label: string): string {
  return UNAVAILABLE_HINTS[label] ?? `Connect ${label.toLowerCase()} for a better review`
}

export function DataSourcesSection({
  dataSources,
  documents,
  onUpdate,
}: {
  dataSources: DataSource[]
  documents: PitchDocument[]
  onUpdate?: () => void
}) {
  const uploadedDocs = documents.filter((d) => d.uploaded)
  const availableSources = dataSources.filter((s) => s.available)
  const unavailableSources = dataSources.filter((s) => !s.available)

  return (
    <div className="px-5 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="size-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-foreground">Data sources</span>
        </div>
        {onUpdate && (
          <button
            type="button"
            onClick={onUpdate}
            className="rounded px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-white/[0.06] hover:text-foreground"
          >
            Update
          </button>
        )}
      </div>
      <div className="mt-2.5 space-y-2">
        {availableSources.map((s) => (
          <div key={s.label}>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="size-3.5 text-emerald-400" />
              <span className="text-sm text-foreground/80">{s.label}</span>
            </div>
            {s.label === 'Documents' && uploadedDocs.length > 0 && (
              <div className="ml-5.5 mt-1 space-y-1 border-l border-border/50 pl-3">
                {uploadedDocs.map((doc) => (
                  <div key={doc.name} className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{doc.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {unavailableSources.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {unavailableSources.map((s) => (
            <div
              key={s.label}
              className="flex items-center justify-between gap-3 rounded-lg bg-amber-400/[0.06] px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <Lightbulb className="size-3.5 shrink-0 text-amber-400/70" />
                <p className="text-xs text-amber-200/70">{unavailableHint(s.label)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
